import React, { useState, useEffect } from 'react';
import TypeInfo from './TypeInfo';
import type {
  NodeInstance,
  NodeType,
  RelationshipType,
} from '../types';
import {
  expandTypeTargets,
  getPairingSourceId,
  getPairingTargetId,
  getRelationshipNameRuleKind,
  getRelationshipPrefix,
  matchesTypePattern,
  relationshipToPairings,
  resolveRelationshipNameFromRule,
} from '../utils/relationshipRules';
import './NodeDetail.css';

type DetailSectionKey = 'description' | 'summary' | 'particular';

interface AddNodeFormData {
  typeId: string;
  values: Record<string, string>;
}

interface NodeDetailProps {
  node: NodeInstance | null;
  graphNodes: NodeInstance[];
  relationshipRules: RelationshipType[];
  nodeTypes: NodeType[];
  onOpenBFOLegend?: () => void;
  onEditNode: (nodeData: Pick<NodeInstance, 'id'> & Partial<NodeInstance>) => Promise<void>;
  onSummarizeNode?: (node: NodeInstance) => Promise<void>;
  onAddNode: (nodeData: Pick<NodeInstance, 'id' | 'name' | 'description' | 'type'> & { properties?: Record<string, string> }) => Promise<void>;
  onStartRelationshipCreation: (args: {
    sourceNode: NodeInstance;
    relType: RelationshipType;
    relationshipName: string;
    relationshipDirection: 'incoming' | 'outgoing';
    candidateNodeIds: string[];
  }) => void;
  onCancelRelationshipCreation: () => void;
  /** When false, save, add-child, and delete are hidden (viewer role). */
  canEdit: boolean;
  onDeleteNode?: (node: NodeInstance) => void;
}

const RESERVED_DETAIL_FIELDS = new Set(['id', 'name', 'type', 'description']);
const CORE_ADD_NODE_FIELDS = ['id', 'name', 'description'];
const PARTICULAR_FIELD_KEYS = new Set(['external_id', 'location']);

const toFieldLabel = (fieldKey: string): string =>
  fieldKey
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatRelationshipRuleLabel = (ruleName: string): string => {
  const kind = getRelationshipNameRuleKind(ruleName);
  if (kind === 'any') {
    return 'Any Relationship';
  }
  if (kind === 'prefix') {
    const prefix = getRelationshipPrefix(ruleName);
    return `Any ${prefix}* Relationship`;
  }
  return ruleName;
};

const getNodeContextValue = (currentNode: NodeInstance, key: string): string => {
  const rootValue = (currentNode as unknown as Record<string, unknown>)[key];
  if (typeof rootValue === 'string' && rootValue.trim() !== '') {
    return rootValue;
  }
  const propertyValue = currentNode.properties?.[key];
  if (typeof propertyValue === 'string') {
    return propertyValue;
  }
  if (typeof propertyValue === 'number' || typeof propertyValue === 'boolean') {
    return String(propertyValue);
  }
  return '';
};

const resolveLocationTemplate = (template: string, currentNode: NodeInstance): string => {
  const withDoubleBraceTokens = template.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_match, token: string) =>
    getNodeContextValue(currentNode, token)
  );
  return withDoubleBraceTokens.replace(/\{\s*([\w.-]+)\s*\}/g, (_match, token: string) =>
    getNodeContextValue(currentNode, token)
  );
};

const isHttpLocation = (value: string): boolean => /^https?:\/\//i.test(value.trim());

interface AllowedRelationshipOption {
  relType: RelationshipType;
  counterpartType: string;
  counterpartTypeId: string;
  relationshipRuleName: string;
  relationshipDirection: 'incoming' | 'outgoing';
}

/**
 * NodeDetail component — Displays details of a selected node.
 * Each editable field is read-only until clicked; only one field is editable
 * at a time. The Save button is disabled until the value actually changes.
 */
const NodeDetail: React.FC<NodeDetailProps> = ({
  node,
  graphNodes,
  relationshipRules,
  nodeTypes,
  onOpenBFOLegend,
  onEditNode,
  onSummarizeNode,
  onAddNode,
  onStartRelationshipCreation,
  onCancelRelationshipCreation,
  canEdit,
  onDeleteNode,
}) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSummarizing, setIsSummarizing] = useState<boolean>(false);
  const [summaryMessage, setSummaryMessage] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [typeInfo, setTypeInfo] = useState<NodeType | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<DetailSectionKey, boolean>>({
    description: false,
    summary: false,
    particular: false,
  });

  // State for add-node UI
  const [showAddNode, setShowAddNode] = useState<boolean>(false);
  const [addNodeForm, setAddNodeForm] = useState<AddNodeFormData>({
    typeId: '',
    values: {},
  });
  const [addNodeError, setAddNodeError] = useState<string | null>(null);

  // State for add-relationship UI — stores array index as string for <select> value
  const [addRelationshipType, setAddRelationshipType] = useState<string>('');
  const [relationshipError, setRelationshipError] = useState<string | null>(null);
  const [customRelationshipName, setCustomRelationshipName] = useState<string>('');

  // Clear editing state when node changes
  useEffect(() => {
    setEditingField(null);
    setEditValue('');
    setError(null);
    setSummaryMessage(null);
    setSummaryError(null);
    setShowAddNode(false);
    setAddNodeForm({ typeId: '', values: {} });
    setAddNodeError(null);
    setAddRelationshipType('');
    setRelationshipError(null);
    setCustomRelationshipName('');
    setCollapsedSections({ description: false, summary: false, particular: false });
    onCancelRelationshipCreation();
  }, [node, onCancelRelationshipCreation]);

  const toggleSection = (section: DetailSectionKey): void => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Load type info for selected node
  useEffect(() => {
    if (node && node.type) {
      const typeId = node.type === 'SchemaType' ? node.id : node.type;
      const t = nodeTypes.find((t) => t.type === typeId) ?? null;
      setTypeInfo(t);
    } else {
      setTypeInfo(null);
    }
  }, [node, nodeTypes]);

  // Start editing a field
  const startEditField = (fieldKey: string, currentValue: string): void => {
    if (!canEdit || isSaving) return;
    setEditingField(fieldKey);
    setEditValue(currentValue);
    setError(null);
  };

  // Cancel editing — restores original display
  const handleCancelEdit = (): void => {
    setEditingField(null);
    setEditValue('');
    setError(null);
  };

  // Save the single edited field
  const handleSaveField = async (fieldKey: string, storage: 'root' | 'property' = 'root'): Promise<void> => {
    if (!node) return;
    try {
      setIsSaving(true);
      // Send only the node id and the single changed field.
      // The backend enforces exactly one field per update proposal, so we must NOT
      // spread the entire node object here.
      await onEditNode(
        storage === 'property'
          ? { id: node.id, properties: { [fieldKey]: editValue } }
          : { id: node.id, [fieldKey]: editValue }
      );
      setEditingField(null);
      setEditValue('');
    } catch (err) {
      console.error('Failed to save field:', err);
      setError((err as Error).message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const selectedAddNodeType = React.useMemo(
    () => nodeTypes.find((t) => t.type === addNodeForm.typeId) ?? null,
    [nodeTypes, addNodeForm.typeId]
  );

  const addNodeSchemaFieldNames = React.useMemo(() => {
    const props = (selectedAddNodeType?.properties_schema as { properties?: Record<string, unknown> } | undefined)?.properties;
    if (!props) return [] as string[];
    return Object.keys(props).filter((fieldName) => fieldName !== 'type');
  }, [selectedAddNodeType]);

  const addNodeRequiredFields = React.useMemo(() => {
    const required = (selectedAddNodeType?.properties_schema as { required?: string[] } | undefined)?.required ?? [];
    const requiredSet = new Set<string>([...CORE_ADD_NODE_FIELDS, ...required.filter((r) => r !== 'type')]);
    return Array.from(requiredSet);
  }, [selectedAddNodeType]);

  const handleAddNodeTypeChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const typeId = e.target.value;
    setAddNodeForm({ typeId, values: { id: '', name: '', description: '' } });
    setAddNodeError(null);
  };

  const handleAddNodeFieldChange = (field: string, value: string): void => {
    setAddNodeForm((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [field]: value,
      },
    }));
    setAddNodeError(null);
  };

  const handleAddNode = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setAddNodeError(null);
    if (!addNodeForm.typeId) {
      setAddNodeError('Please select a node type.');
      return;
    }

    for (const field of addNodeRequiredFields) {
      if (!(addNodeForm.values[field] || '').trim()) {
        setAddNodeError(`${toFieldLabel(field)} is required.`);
        return;
      }
    }

    setIsSaving(true);
    try {
      await onAddNode({
        id: (addNodeForm.values.id || '').trim(),
        name: (addNodeForm.values.name || '').trim(),
        description: (addNodeForm.values.description || '').trim(),
        type: addNodeForm.typeId,
        properties: Object.fromEntries(
          Object.entries(addNodeForm.values)
            .filter(([key]) => !CORE_ADD_NODE_FIELDS.includes(key))
            .map(([key, value]) => [key, (value || '').trim()])
        ),
      });
      setShowAddNode(false);
      setAddNodeForm({ typeId: '', values: {} });
    } catch (err) {
      setAddNodeError((err as Error).message || 'Failed to add node.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSummarizeNode = async (): Promise<void> => {
    if (!node || !onSummarizeNode) {
      return;
    }
    try {
      setIsSummarizing(true);
      setSummaryMessage(null);
      setSummaryError(null);
      await onSummarizeNode(node);
      setSummaryMessage('Summary session submitted. Review the generated proposal session to apply changes.');
    } catch (err) {
      console.error('Failed to summarize node:', err);
      setSummaryError((err as Error).message || 'Failed to trigger summary generation.');
    } finally {
      setIsSummarizing(false);
    }
  };

  // Compute Add Relationship options for the current node in both directions.
  const allowedRelationshipTypes: AllowedRelationshipOption[] = React.useMemo(() => {
    if (!node || !node.type || !relationshipRules || !nodeTypes) return [];
    const result: AllowedRelationshipOption[] = [];
    const seen = new Set<string>();
    const allNodeTypeIds = nodeTypes.map((nt) => nt.type);

    relationshipRules.forEach((rel) => {
      relationshipToPairings(rel).forEach((pair) => {
        const sourcePattern = getPairingSourceId(pair);
        const targetPattern = getPairingTargetId(pair);

        // Incoming option: new node -> current node
        if (matchesTypePattern(targetPattern, node.type)) {
          const allowedSourceTypeIds = expandTypeTargets(sourcePattern, allNodeTypeIds);
          allowedSourceTypeIds.forEach((sourceTypeId) => {
            const isSelfType = sourceTypeId.toLowerCase() === node.type.toLowerCase();
            const key = isSelfType
              ? `${rel.name}|self|${sourceTypeId}`
              : `${rel.name}|incoming|${sourceTypeId}`;
            if (seen.has(key)) {
              return;
            }
            seen.add(key);

            const childTypeObj = nodeTypes.find((nt) => nt.type === sourceTypeId);
            result.push({
              relType: rel,
              counterpartType: childTypeObj ? childTypeObj.name : sourceTypeId,
              counterpartTypeId: sourceTypeId,
              relationshipRuleName: rel.name,
              relationshipDirection: 'incoming',
            });
          });
        }

        // Outgoing option: current node -> new node
        if (matchesTypePattern(sourcePattern, node.type)) {
          const allowedTargetTypeIds = expandTypeTargets(targetPattern, allNodeTypeIds);
          allowedTargetTypeIds.forEach((targetTypeId) => {
            const isSelfType = targetTypeId.toLowerCase() === node.type.toLowerCase();
            const key = isSelfType
              ? `${rel.name}|self|${targetTypeId}`
              : `${rel.name}|outgoing|${targetTypeId}`;
            if (seen.has(key)) {
              return;
            }
            seen.add(key);

            const childTypeObj = nodeTypes.find((nt) => nt.type === targetTypeId);
            result.push({
              relType: rel,
              counterpartType: childTypeObj ? childTypeObj.name : targetTypeId,
              counterpartTypeId: targetTypeId,
              relationshipRuleName: rel.name,
              relationshipDirection: 'outgoing',
            });
          });
        }
      });
    });
    return result;
  }, [node, relationshipRules, nodeTypes]);

  const selectedRelationship = React.useMemo(() => {
    if (addRelationshipType === '') {
      return null;
    }
    const idx = parseInt(addRelationshipType, 10);
    if (Number.isNaN(idx)) {
      return null;
    }
    return allowedRelationshipTypes[idx] ?? null;
  }, [addRelationshipType, allowedRelationshipTypes]);

  const selectedRelationshipRuleKind = selectedRelationship
    ? getRelationshipNameRuleKind(selectedRelationship.relationshipRuleName)
    : 'fixed';

  const selectedRelationshipPrefix = selectedRelationship
    ? getRelationshipPrefix(selectedRelationship.relationshipRuleName)
    : '';

  const relationshipCandidateIds = React.useMemo(() => {
    if (!node || !selectedRelationship) {
      return [] as string[];
    }
    return graphNodes
      .filter((n) => n.id !== node.id)
      .filter((n) => n.type.toLowerCase() === selectedRelationship.counterpartTypeId.toLowerCase())
      .map((n) => n.id);
  }, [node, selectedRelationship, graphNodes]);

  const relationshipCandidateKey = React.useMemo(
    () => [...relationshipCandidateIds].sort().join('|'),
    [relationshipCandidateIds]
  );
  const lastRelCreationRequestKeyRef = React.useRef<string>('');
  const relationshipCreationActiveRef = React.useRef(false);
  const onStartRelationshipCreationRef = React.useRef(onStartRelationshipCreation);
  const onCancelRelationshipCreationRef = React.useRef(onCancelRelationshipCreation);

  useEffect(() => {
    onStartRelationshipCreationRef.current = onStartRelationshipCreation;
  }, [onStartRelationshipCreation]);

  useEffect(() => {
    onCancelRelationshipCreationRef.current = onCancelRelationshipCreation;
  }, [onCancelRelationshipCreation]);

  useEffect(() => {
    if (!node || !selectedRelationship) {
      setRelationshipError(null);
      lastRelCreationRequestKeyRef.current = '';
      if (relationshipCreationActiveRef.current) {
        relationshipCreationActiveRef.current = false;
        onCancelRelationshipCreationRef.current();
      }
      return;
    }

    const resolvedRelationshipName = resolveRelationshipNameFromRule(
      selectedRelationship.relationshipRuleName,
      customRelationshipName
    );

    if (!resolvedRelationshipName.value) {
      setRelationshipError(resolvedRelationshipName.error || 'Invalid relationship name.');
      lastRelCreationRequestKeyRef.current = '';
      if (relationshipCreationActiveRef.current) {
        relationshipCreationActiveRef.current = false;
        onCancelRelationshipCreationRef.current();
      }
      return;
    }

    const requestKey = [
      node.id,
      selectedRelationship.relType.name,
      selectedRelationship.relationshipDirection,
      resolvedRelationshipName.value,
      relationshipCandidateKey,
    ].join('::');

    if (lastRelCreationRequestKeyRef.current === requestKey) {
      return;
    }
    lastRelCreationRequestKeyRef.current = requestKey;

    relationshipCreationActiveRef.current = true;
    setRelationshipError(null);
    onStartRelationshipCreationRef.current({
      sourceNode: node,
      relType: selectedRelationship.relType,
      relationshipName: resolvedRelationshipName.value,
      relationshipDirection: selectedRelationship.relationshipDirection,
      candidateNodeIds: relationshipCandidateIds,
    });
  }, [
    node,
    selectedRelationship,
    customRelationshipName,
    relationshipCandidateIds,
    relationshipCandidateKey,
  ]);

  const dynamicSchemaFields = React.useMemo(() => {
    const props = (typeInfo?.properties_schema as { properties?: Record<string, unknown> } | undefined)?.properties;
    if (!props) return [] as string[];
    return Object.keys(props).filter((fieldName) => !RESERVED_DETAIL_FIELDS.has(fieldName) && !PARTICULAR_FIELD_KEYS.has(fieldName));
  }, [typeInfo]);

  const isParticularNode = React.useMemo(
    () => typeInfo?.labels?.some((label) => label.toLowerCase() === 'particular') ?? false,
    [typeInfo]
  );

  const particularMissingFields = node?.particular?.missing_fields ?? [];
  const summaryStateLabel = (node?.summary_state || 'missing').trim() || 'missing';
  const hasSummary = Boolean(node?.semantic_summary?.trim());
  const summaryActionLabel = hasSummary ? 'Re-run summary' : 'Generate summary';

  /** Renders a single read-only-until-clicked editable field. */
  const renderEditableField = (
    fieldId: string,
    label: string,
    fieldKey: string,
    multiline = false,
    storage: 'root' | 'property' = 'root',
    collapsibleSection?: DetailSectionKey
  ): React.ReactElement => {
    const originalValue = storage === 'property'
      ? String(node?.properties?.[fieldKey] ?? '')
      : String((node as unknown as Record<string, unknown>)[fieldKey] ?? '');
    const isEditing = editingField === fieldKey;
    const pendingCount = node?.pending_property_names?.filter((n) => n === fieldKey).length ?? 0;
    const valueChanged = isEditing && editValue !== originalValue;
    const isCollapsed = collapsibleSection ? collapsedSections[collapsibleSection] : false;
    const labelClassName = collapsibleSection ? 'detail-section__title detail-section__title--toggle' : undefined;
    const resolvedLocation = fieldKey === 'location' && node
      ? resolveLocationTemplate(originalValue, node)
      : '';
    const hasResolvedHttpLocation = resolvedLocation !== '' && isHttpLocation(resolvedLocation);

    return (
      <div className="form-group" key={fieldKey}>
        <label
          htmlFor={fieldId}
          className={labelClassName}
          onClick={collapsibleSection ? () => toggleSection(collapsibleSection) : undefined}
        >
          {label}
          {pendingCount > 0 && (
            <span
              className="pending-badge"
              title={`${pendingCount} update${pendingCount > 1 ? 's' : ''} awaiting curator review`}
            >
              {pendingCount === 1 ? 'pending review' : `${pendingCount}\u00d7 pending`}
            </span>
          )}
        </label>
        {isEditing ? (
          <>
            {multiline ? (
              <textarea
                id={fieldId}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                rows={3}
                autoFocus
              />
            ) : (
              <input
                id={fieldId}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
              />
            )}
            {canEdit && (
              <div className="inline-field-actions">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!valueChanged || isSaving}
                  onClick={() => handleSaveField(fieldKey, storage)}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        ) : !isCollapsed ? (
          <div className="readonly-composite-value">
            <span
              id={fieldId}
              className={canEdit ? 'editable-value' : 'readonly-value'}
              onClick={() => startEditField(fieldKey, originalValue)}
              title={canEdit ? 'Click to edit' : undefined}
            >
              {originalValue || <em className="empty-value">(empty)</em>}
            </span>
            {hasResolvedHttpLocation && (
              <a
                className="field-link"
                href={resolvedLocation}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open link
              </a>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  if (!node) {
    return (
      <div className="node-detail">
        <p className="no-selection">No node selected</p>
        {canEdit && (
          <div className="node-actions-wrap">
            <div className="node-actions-row">
              <button
                type="button"
                className="btn-approve"
                onClick={() => setShowAddNode((v) => !v)}
              >
                Add Node
              </button>
            </div>

            {showAddNode && (
              <form className="add-related-panel add-child-form" onSubmit={handleAddNode}>
                <div className="form-group">
                  <label htmlFor="add-node-type">Node Type</label>
                  <select
                    id="add-node-type"
                    value={addNodeForm.typeId}
                    onChange={handleAddNodeTypeChange}
                  >
                    <option value="">Select type definition</option>
                    {nodeTypes.map((item) => (
                      <option key={item.type} value={item.type}>
                        {item.name || item.type}
                      </option>
                    ))}
                  </select>
                </div>

                {addNodeForm.typeId !== '' && (
                  <>
                    {Array.from(new Set([...CORE_ADD_NODE_FIELDS, ...addNodeSchemaFieldNames])).map((fieldName) => (
                      <div className="form-group" key={`add-node-${fieldName}`}>
                        <label htmlFor={`add-node-${fieldName}`}>
                          {toFieldLabel(fieldName)}
                          {addNodeRequiredFields.includes(fieldName) && <span className="required">*</span>}
                        </label>
                        {fieldName.toLowerCase().includes('description') ? (
                          <textarea
                            id={`add-node-${fieldName}`}
                            rows={2}
                            value={addNodeForm.values[fieldName] ?? ''}
                            onChange={(e) => handleAddNodeFieldChange(fieldName, e.target.value)}
                          />
                        ) : (
                          <input
                            id={`add-node-${fieldName}`}
                            type="text"
                            value={addNodeForm.values[fieldName] ?? ''}
                            onChange={(e) => handleAddNodeFieldChange(fieldName, e.target.value)}
                          />
                        )}
                      </div>
                    ))}
                  </>
                )}

                {addNodeError && (
                  <div className="error-message" role="alert">
                    <strong>Error:</strong> {addNodeError}
                  </div>
                )}

                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={isSaving || !addNodeForm.typeId}>
                    {isSaving ? 'Adding...' : 'Add Node'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="node-detail">
      {editingField === 'name' ? (
        <div className="node-title-editor">
          <input
            id="edit-node-title"
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            autoFocus
          />
          <div className="inline-field-actions">
            <button
              type="button"
              className="btn-primary"
              disabled={editValue === String(node.name ?? '') || isSaving}
              onClick={() => handleSaveField('name')}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <h3
          className={canEdit ? 'editable-title' : ''}
          onClick={() => startEditField('name', String(node.name ?? ''))}
          title={canEdit ? 'Click to edit name' : undefined}
        >
          {node.name || node.id}
        </h3>
      )}

      {/* Read-only ID */}
      <div className="form-group inline-label">
        <label htmlFor="edit-node-id">ID</label>
        <span id="edit-node-id" className="readonly-value">
          {node.id}
        </span>
      </div>

      <section className="detail-section">
        {renderEditableField('edit-node-description', 'Description', 'description', true, 'root', 'description')}
      </section>

      <section className="detail-section detail-section--summary">
        <div className="form-group">
          <div className="detail-section__header detail-section__header--summary">
            <label className="detail-section__title detail-section__title--toggle" onClick={() => toggleSection('summary')}>Summary</label>
            <div className="detail-section__header-actions">
              {onSummarizeNode && (
                <button
                  type="button"
                  className="summary-icon-button"
                  onClick={() => {
                    void handleSummarizeNode();
                  }}
                  disabled={isSummarizing}
                  title={`${summaryActionLabel} (${summaryStateLabel})`}
                  aria-label={`${summaryActionLabel} (${summaryStateLabel})`}
                >
                  <i className="modus-icons" aria-hidden="true">file_edit</i>
                </button>
              )}
            </div>
          </div>
          {!collapsedSections.summary && (
            <div id="node-summary-section">
              <div id="node-semantic-summary" className="readonly-value readonly-value--multiline summary-content">
                {hasSummary ? node.semantic_summary : <em className="empty-value">No summary</em>}
              </div>
            </div>
          )}
        </div>
        {!collapsedSections.summary && summaryMessage && (
          <div className="summary-status summary-status--done" role="status">
            {summaryMessage}
          </div>
        )}
        {!collapsedSections.summary && summaryError && (
          <div className="summary-status summary-status--error" role="alert">
            {summaryError}
          </div>
        )}
        {!collapsedSections.summary && node.aliases && node.aliases.length > 0 && (
          <div className="node-aliases">
            <span className="node-aliases__label">Aliases:</span>
            <div className="node-aliases__list">
              {node.aliases.map((alias) => (
                <span key={alias} className="node-aliases__item">{alias}</span>
              ))}
            </div>
          </div>
        )}
      </section>

      {isParticularNode && (
        <section className="detail-section detail-section--particular">
          <div className="detail-section__header">
            <h4 className="detail-section__title detail-section__title--toggle" onClick={() => toggleSection('particular')}>Particular</h4>
            {particularMissingFields.length > 0 && (
              <span className="detail-section__warning">
                Missing {particularMissingFields.map(toFieldLabel).join(', ')}
              </span>
            )}
          </div>
          {!collapsedSections.particular && (
            <div id="node-particular-section">
              {renderEditableField('edit-node-external-id', 'External ID', 'external_id', false, 'property')}
              {renderEditableField('edit-node-location', 'Location', 'location', false, 'property')}
            </div>
          )}
        </section>
      )}

      {dynamicSchemaFields.map((fieldKey) =>
        renderEditableField(`edit-node-${fieldKey}`, toFieldLabel(fieldKey), fieldKey, false, 'property')
      )}

      {error && (
        <div className="error-message" role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {typeInfo && (
        <div style={{ marginTop: 16 }}>
          <TypeInfo type={typeInfo} relationshipRules={relationshipRules} onOpenBFOLegend={onOpenBFOLegend} />
        </div>
      )}

      {canEdit && (
        <div className="node-actions-wrap">
          <div className="node-actions-row">
            {onDeleteNode && (
              <button
                type="button"
                className="btn-danger"
                onClick={() => onDeleteNode(node)}
              >
                Delete
              </button>
            )}
          </div>

          <div className="add-related-panel">
            <select
              id="add-child-type"
              value={addRelationshipType}
              onChange={(e) => {
                setAddRelationshipType(e.target.value);
                setCustomRelationshipName('');
                setRelationshipError(null);
              }}
              disabled={allowedRelationshipTypes.length === 0}
              title={allowedRelationshipTypes.length === 0 ? 'No relationship types available for this node type' : undefined}
            >
              <option value="">Add relationship</option>
              {allowedRelationshipTypes.map((item, idx) => (
                <option key={item.counterpartType + '-' + idx} value={idx}>
                  {item.relationshipDirection === 'incoming'
                    ? `${item.counterpartType} -> ${node.type}`
                    : `${node.type} -> ${item.counterpartType}`} ({formatRelationshipRuleLabel(item.relationshipRuleName)})
                </option>
              ))}
            </select>

            {addRelationshipType !== '' && (
              <div className="add-child-form">
                {selectedRelationshipRuleKind !== 'fixed' && (
                  <div className="form-group">
                    <label htmlFor="add-child-rel-name">Relationship Name</label>
                    <input
                      id="add-child-rel-name"
                      type="text"
                      value={customRelationshipName}
                      onChange={(e) => {
                        setCustomRelationshipName(e.target.value.toUpperCase());
                        setRelationshipError(null);
                      }}
                      placeholder={
                        selectedRelationshipRuleKind === 'any'
                          ? 'CAPITAL_LETTERS_SEPARATED_WITH_UNDERSCORES'
                          : `${selectedRelationshipPrefix}YOUR_SUFFIX`
                      }
                    />
                  </div>
                )}
                <p className="field-hint" style={{ marginTop: 8 }}>
                  {relationshipCandidateIds.length > 0
                    ? `Select one of ${relationshipCandidateIds.length} highlighted candidate node${relationshipCandidateIds.length === 1 ? '' : 's'} in the graph to create this relationship.`
                    : 'No candidate nodes are currently visible in the graph for this relationship.'}
                </p>
                {relationshipError && (
                  <div className="error-message" role="alert">
                    <strong>Error:</strong> {relationshipError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NodeDetail;
